'use client';

import ProductsListingSkeleton from '@/components/ProductsListingSkeleton';
import { SOFT_NAV_SKELETON_TIMEOUT_MS } from '@/lib/instantRouteTransition';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type ListingNavContextValue = {
  pending: boolean;
  beginListingNavigation: () => void;
  endListingNavigation: () => void;
};

const ListingNavContext = createContext<ListingNavContextValue | null>(null);

let listingBeginHandler: (() => void) | null = null;

export function beginUrunlerListingNavigation() {
  listingBeginHandler?.();
}

export function UrunlerListingNavigationProvider({
  children,
  navigationKey,
}: {
  children: ReactNode;
  /**
   * Server render tamamlanınca değişen bir key.
   * searchParams client tarafında hemen değişebildiği için ona güvenmiyoruz.
   */
  navigationKey: string;
}) {
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setPending(false);
  }, [navigationKey]);

  useEffect(() => {
    if (!pending) return;
    const t = window.setTimeout(() => setPending(false), SOFT_NAV_SKELETON_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [pending]);

  useEffect(() => {
    const onAbort = () => setPending(false);
    window.addEventListener('popstate', onAbort);
    return () => window.removeEventListener('popstate', onAbort);
  }, []);

  const beginListingNavigation = useCallback(() => {
    setPending(true);
  }, []);

  useEffect(() => {
    listingBeginHandler = beginListingNavigation;
    return () => {
      if (listingBeginHandler === beginListingNavigation) listingBeginHandler = null;
    };
  }, [beginListingNavigation]);

  const endListingNavigation = useCallback(() => {
    setPending(false);
  }, []);

  const value = useMemo(
    () => ({ pending, beginListingNavigation, endListingNavigation }),
    [pending, beginListingNavigation, endListingNavigation],
  );

  return <ListingNavContext.Provider value={value}>{children}</ListingNavContext.Provider>;
}

export function useUrunlerListingNavigationOptional(): ListingNavContextValue | null {
  return useContext(ListingNavContext);
}

export function UrunlerListingGate({ children }: { children: ReactNode }) {
  const ctx = useUrunlerListingNavigationOptional();
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [lastHeight, setLastHeight] = useState<number>(0);

  useLayoutEffect(() => {
    if (ctx?.pending) return;

    const el = measureRef.current;
    if (!el) return;

    const update = () => {
      const next = Math.max(0, Math.round(el.getBoundingClientRect().height));
      if (next > 0) setLastHeight(next);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ctx?.pending]);

  const skeletonMinHeightPx = Math.max(lastHeight, 420);

  return (
    <div className="relative w-full min-w-0">
      {/* Render real list only when not pending, but keep last height to avoid layout jump. */}
      {!ctx?.pending ? (
        <div ref={measureRef}>{children}</div>
      ) : (
        <div
          className="relative z-[60] bg-hzc_beige_200 min-h-[40vh] md:min-h-[45vh] w-full min-w-0"
          style={{ minHeight: `${skeletonMinHeightPx}px` }}
        >
          <ProductsListingSkeleton />
        </div>
      )}
    </div>
  );
}
